import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMenu } from '../MenuContext';
import { useUser } from '../UserContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { MenuItem } from 'types/menuTypes';
import {
  extractDocumentId,
  findFirstValidMenu,
  findMenuPathByDocumentId,
  findMenuPathByUrl,
  findSmartFallback,
} from 'utils/menuUtils';

interface MenuSelectionType {
  firstLevelMenu: MenuItem | null;
  secondLevelMenu: MenuItem | null;
  thirdLevelMenu: MenuItem | null;
  setFirstLevelMenu: (menu: MenuItem | null) => void;
  setSecondLevelMenu: (menu: MenuItem | null) => void;
  setThirdLevelMenu: (menu: MenuItem | null) => void;
  selectMenuByDocumentId: (
    documentId: string,
    shouldNavigate?: boolean,
    pathOverride?: MenuItem[],
    path?: string,
    preserveLevel?: boolean
  ) => void;
  selectMenuByPath: (path: string) => void;
  currentLevel: 'first' | 'second' | 'third' | null;
  activeModulePath: string | null;
  activeDocumentId: string | null;
  isInitialized: boolean;
}

const MenuSelectionContext = createContext<
  | (MenuSelectionType & {
      isModalSubMenuOpen: boolean;
      setIsModalSubMenuOpen: (open: boolean) => void;
    })
  | undefined
>(undefined);

export const MenuSelectionProvider = ({ children }: { children: React.ReactNode }) => {
  const { menu } = useMenu();
  const { token } = useUser();
  const menuRoutes = useMenuRoutes();
  const navigate = useNavigate();
  const isInitializing = useRef(false);

  const [firstLevelMenu, setFirstLevelMenu] = useState<MenuItem | null>(null);
  const [secondLevelMenu, setSecondLevelMenu] = useState<MenuItem | null>(null);
  const [thirdLevelMenu, setThirdLevelMenu] = useState<MenuItem | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Novo: Estado global do ModalSubMenu, restaurado do localStorage
  const [isModalSubMenuOpen, setIsModalSubMenuOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem('selectedMenus');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return !!parsed.secondLevel;
      } catch {
        return false;
      }
    }
    return false;
  });

  // Sempre que mudar o menu de segundo nível, atualiza o estado do modal
  React.useEffect(() => {
    setIsModalSubMenuOpen(!!secondLevelMenu);
  }, [secondLevelMenu]);

  // Salva no localStorage sempre que mudar
  React.useEffect(() => {
    localStorage.setItem(
      'selectedMenus',
      JSON.stringify({
        firstLevel: firstLevelMenu,
        secondLevel: secondLevelMenu,
        thirdLevel: thirdLevelMenu,
        isModalSubMenuOpen,
      })
    );
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu, isModalSubMenuOpen]);

  // Seleciona menu por documentId
  const selectMenuByDocumentId = (
    documentId: string,
    shouldNavigate = false,
    pathOverride?: MenuItem[],
    path?: string,
    preserveLevel?: boolean
  ) => {
    if (!menu.length || !documentId) {
      console.log('[MenuSelectionContext] Menu vazio ou documentId inválido');
      return;
    }

    console.log('[MenuSelectionContext] Selecionando menu:', {
      documentId,
      shouldNavigate,
      hasPathOverride: !!pathOverride,
      path,
      preserveLevel,
      currentActiveDocumentId: activeDocumentId,
      currentLevel,
    });

    // Se temos um pathOverride, usamos ele diretamente
    if (pathOverride) {
      console.log(
        '[MenuSelectionContext] Usando pathOverride:',
        pathOverride.map(m => m.label)
      );

      // Verifica se é uma sala de reunião
      const isMeetingRoom = pathOverride[0]?.feature?.dynamic_component === 'MEETING_ROOM';
      const isMeetingRoomRoom = pathOverride[1]?.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

      if (isMeetingRoom && pathOverride[1]) {
        // Se for uma sala de reunião, força o nível para third
        setFirstLevelMenu(pathOverride[0]);
        setSecondLevelMenu(pathOverride[0]); // Mantém o menu pai como segundo nível
        setThirdLevelMenu(pathOverride[1]); // Define a sala como terceiro nível

        // Força o nível do menu para third
        const event = new CustomEvent('forceMenuLevel', { detail: 'third' });
        window.dispatchEvent(event);

        // Garante que o activeDocumentId seja definido
        if (shouldNavigate) {
          const lastMenu = pathOverride[1];
          if (lastMenu?.path) {
            console.log('[MenuSelectionContext] Navegando para:', lastMenu.path);
            navigate(lastMenu.path);
          }
        }
        return;
      }

      // Para outros menus, mantém o comportamento normal
      setFirstLevelMenu(pathOverride[0] || null);
      setSecondLevelMenu(pathOverride[1] || null);
      setThirdLevelMenu(pathOverride[2] || null);

      if (shouldNavigate) {
        const lastMenu = pathOverride[2] || pathOverride[1] || pathOverride[0];
        if (lastMenu?.path) {
          console.log('[MenuSelectionContext] Navegando para:', lastMenu.path);
          navigate(lastMenu.path);
        }
      }
      return;
    }

    console.log('[MenuSelectionContext] Buscando menu com findMenuPathByDocumentId:', {
      documentId,
      path,
    });
    const menuPath = findMenuPathByDocumentId(menu, documentId, path);
    if (!menuPath) {
      console.warn('[MenuSelectionContext] Menu não encontrado:', documentId, path);
      return;
    }

    console.log(
      '[MenuSelectionContext] Menu encontrado:',
      menuPath.items.map(m => m.label)
    );

    // Verifica se é uma sala de reunião
    const isMeetingRoom = menuPath.items[0]?.feature?.dynamic_component === 'MEETING_ROOM';
    const isMeetingRoomRoom = menuPath.items[1]?.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

    if (isMeetingRoom && menuPath.items[1]) {
      // Se for uma sala de reunião, força o nível para third
      setFirstLevelMenu(menuPath.items[0]);
      setSecondLevelMenu(menuPath.items[0]); // Mantém o menu pai como segundo nível
      setThirdLevelMenu(menuPath.items[1]); // Define a sala como terceiro nível

      // Força o nível do menu para third
      const event = new CustomEvent('forceMenuLevel', { detail: 'third' });
      window.dispatchEvent(event);

      // Garante que o activeDocumentId seja definido
      if (shouldNavigate) {
        const lastMenu = menuPath.items[1];
        if (lastMenu?.path) {
          console.log('[MenuSelectionContext] Navegando para:', lastMenu.path);
          navigate(lastMenu.path);
        }
      }
      return;
    }

    // Para outros menus, preserva o nível atual se solicitado
    const targetLevel = menuPath.items.length;
    const currentLevelNumber =
      currentLevel === 'first'
        ? 1
        : currentLevel === 'second'
          ? 2
          : currentLevel === 'third'
            ? 3
            : 0;

    console.log('[MenuSelectionContext] Níveis:', {
      targetLevel,
      currentLevelNumber,
      currentLevel,
      menuPathLength: menuPath.items.length,
      preserveLevel,
    });

    // Se preserveLevel é true, mantém o nível atual e apenas atualiza o activeDocumentId
    if (preserveLevel) {
      console.log(
        '[MenuSelectionContext] Preservando nível atual conforme solicitado:',
        currentLevel
      );
      // Não altera os menus, apenas garante que o activeDocumentId seja atualizado
      return;
    }

    // Verifica se o path atual já está correto para evitar mudanças desnecessárias
    const lastMenu = menuPath.items[menuPath.items.length - 1];
    const currentPath = window.location.pathname;

    if (lastMenu?.path === currentPath && !shouldNavigate) {
      console.log(
        '[MenuSelectionContext] Path atual já está correto, não fazendo mudanças desnecessárias'
      );
      return;
    }

    // Para outros menus, SEMPRE define os menus corretos baseado no path encontrado
    setFirstLevelMenu(menuPath.items[0] || null);
    setSecondLevelMenu(menuPath.items[1] || null);
    setThirdLevelMenu(menuPath.items[2] || null);

    // Navega se necessário
    if (shouldNavigate) {
      if (lastMenu?.path) {
        console.log('[MenuSelectionContext] Navegando para:', lastMenu.path);
        navigate(lastMenu.path);
      }
    }
  };

  // Seleciona menu por path
  const selectMenuByPath = (path: string) => {
    if (!menu.length || !path) {
      console.log('[MenuSelectionContext] Menu vazio ou path inválido');
      return;
    }

    console.log('[MenuSelectionContext] Selecionando menu por path:', path);

    const menuPath = findMenuPathByUrl(menu, path);
    if (!menuPath) {
      console.warn('[MenuSelectionContext] Menu não encontrado para path:', path);
      return;
    }

    setFirstLevelMenu(menuPath.items[0] || null);
    setSecondLevelMenu(menuPath.items[1] || null);
    setThirdLevelMenu(menuPath.items[2] || null);
  };

  // Derivados simplificados
  const currentLevel = useMemo(() => {
    if (thirdLevelMenu) return 'third';
    if (secondLevelMenu) return 'second';
    if (firstLevelMenu) return 'first';
    return null;
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  const activeModulePath = useMemo(() => {
    const lastMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
    return lastMenu?.path || null;
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  const activeDocumentId = useMemo(() => {
    const lastMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
    if (!lastMenu) return null;

    return extractDocumentId(lastMenu);
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  // Log para debug dos menus selecionados
  useEffect(() => {
    console.log('[MenuSelectionContext] Menus selecionados:', {
      firstLevel: firstLevelMenu?.label,
      secondLevel: secondLevelMenu?.label,
      thirdLevel: thirdLevelMenu?.label,
      currentLevel,
      activeDocumentId,
    });
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu, currentLevel, activeDocumentId]);

  // Inicialização simplificada
  useEffect(() => {
    if (!token || !menu.length || isInitialized || isInitializing.current) return;

    isInitializing.current = true;
    console.log('[MenuSelectionContext] Iniciando...', {
      currentPath: window.location.pathname,
      menuLength: menu.length,
      isInitialized,
    });

    // Aguarda um pouco para garantir que o UserContext terminou de inicializar
    const initializeWithDelay = () => {
      const currentPath = window.location.pathname;

      // Skip initialization for event-invite paths
      const isEventInvitePath = currentPath.startsWith('/invite/event/');
      if (isEventInvitePath) {
        console.log('[MenuSelectionContext] Event-invite path detected, skipping initialization');
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 1. Tenta restaurar pelo path da URL atual
      const menuPathByUrl = findMenuPathByUrl(menu, currentPath);
      if (menuPathByUrl) {
        console.log('[MenuSelectionContext] Restaurando pelo path da URL:', currentPath);
        selectMenuByPath(currentPath);
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 2. Tenta restaurar do localStorage
      const stored = localStorage.getItem('selectedMenus');
      if (stored) {
        try {
          const { activeDocumentId: storedId } = JSON.parse(stored);
          if (storedId) {
            console.log('[MenuSelectionContext] Restaurando do localStorage:', storedId);
            selectMenuByDocumentId(storedId, false);
            setIsInitialized(true);
            isInitializing.current = false;
            return;
          }
        } catch (e) {
          console.warn('[MenuSelectionContext] Erro ao restaurar do localStorage:', e);
        }
      }

      // 3. Verifica se a rota atual é válida antes de usar fallback
      const isCurrentRouteValid =
        menuRoutes.length > 0 && menuRoutes.some(route => route.path === currentPath);
      if (isCurrentRouteValid) {
        console.log(
          '[MenuSelectionContext] Rota atual é válida, aguardando sincronização automática'
        );
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 4. Se não encontrou nada e a rota não é válida, usa fallback inteligente
      const fallback = findSmartFallback(menu, currentPath, currentLevel);
      if (fallback) {
        console.log('[MenuSelectionContext] Usando fallback inteligente:', fallback.path);
        selectMenuByDocumentId(fallback.documentId, false, fallback.items, fallback.path);
        setIsInitialized(true);
      } else {
        // Último recurso: primeiro menu válido (apenas se não estivermos em rota válida)
        const firstValid = findFirstValidMenu(menu);
        if (firstValid) {
          const documentId = extractDocumentId(firstValid);
          if (documentId) {
            console.log('[MenuSelectionContext] Usando primeiro menu válido:', firstValid.label);
            selectMenuByDocumentId(documentId, false);
            setIsInitialized(true);
          }
        }
      }

      isInitializing.current = false;
    };

    // Aguarda 200ms para garantir que o menu está completamente carregado
    setTimeout(initializeWithDelay, 200);
  }, [menu, isInitialized, token, currentLevel, menuRoutes]);

  // Listener para forçar nível do menu
  useEffect(() => {
    const handleForceMenuLevel = (event: CustomEvent) => {
      const level = event.detail;
      console.log('[MenuSelectionContext] Forçando nível do menu para:', level);

      if (level === 'third' && secondLevelMenu) {
        // Se estamos forçando para third e temos um segundo nível,
        // mantemos o segundo nível e forçamos o terceiro
        setThirdLevelMenu(secondLevelMenu);
      }
    };

    const handleUserContextInitialized = (event: CustomEvent) => {
      console.log('[MenuSelectionContext] UserContext inicializado, restaurando navegação');
      const { menu: userMenu } = event.detail;

      // Se o menu do UserContext é diferente do nosso, atualiza
      if (userMenu && JSON.stringify(userMenu) !== JSON.stringify(menu)) {
        console.log('[MenuSelectionContext] Menu atualizado do UserContext');
        // O MenuContext vai atualizar automaticamente
      }

      // Força a restauração da navegação
      if (!isInitialized) {
        console.log('[MenuSelectionContext] Forçando inicialização após UserContext');
        isInitializing.current = false;
        setIsInitialized(false);
      }
    };

    window.addEventListener('forceMenuLevel', handleForceMenuLevel as EventListener);
    window.addEventListener(
      'userContextInitialized',
      handleUserContextInitialized as EventListener
    );

    return () => {
      window.removeEventListener('forceMenuLevel', handleForceMenuLevel as EventListener);
      window.removeEventListener(
        'userContextInitialized',
        handleUserContextInitialized as EventListener
      );
    };
  }, [secondLevelMenu, menu, isInitialized]);

  const value: MenuSelectionType & {
    isModalSubMenuOpen: boolean;
    setIsModalSubMenuOpen: (open: boolean) => void;
  } = {
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    setFirstLevelMenu,
    setSecondLevelMenu,
    setThirdLevelMenu,
    selectMenuByDocumentId,
    selectMenuByPath,
    currentLevel,
    activeModulePath,
    activeDocumentId,
    isInitialized,
    isModalSubMenuOpen,
    setIsModalSubMenuOpen,
  };

  return <MenuSelectionContext.Provider value={value}>{children}</MenuSelectionContext.Provider>;
};

export const useMenuSelection = (): MenuSelectionType => {
  const context = useContext(MenuSelectionContext);
  if (!context) {
    throw new Error('useMenuSelection deve ser usado dentro de um MenuSelectionProvider');
  }
  return context;
};
